import React, { useState, useEffect } from 'react';
import { Container, Grid, Card, Text, Title, Group, Stack, Button, Badge, Image, SimpleGrid, Skeleton, TextInput, Select, NumberInput, Pagination } from '@mantine/core';
import { IconSearch, IconMapPin, IconCurrencyDollar, IconCalendar, IconHome, IconFilter } from '@tabler/icons-react';
import { Link } from 'react-router-dom';

const Properties = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    search: '',
    propertyType: '',
    listingType: '',
    minPrice: '',
    maxPrice: '',
    state: ''
  });

  useEffect(() => {
    fetchProperties();
  }, [currentPage, filters]);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: 12,
        ...Object.fromEntries(Object.entries(filters).filter(([_, value]) => value !== ''))
      });

      const response = await fetch(`/api/properties?${queryParams}`);
      const data = await response.json();
      
      console.log('Properties API response:', data);
      
      if (data.success) {
        console.log('Properties received:', data.data);
        setProperties(data.data || []);
        setTotalPages(data.pagination?.pages || 1);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      propertyType: '',
      listingType: '',
      minPrice: '',
      maxPrice: '',
      state: ''
    });
    setCurrentPage(1);
  };

  return (
    <Container size="xl" py="md">
      <Stack gap="xl">
        <Title order={1} c="green" ta="center">Browse Properties</Title>
        <Text c="dimmed" ta="center" maw={600} mx="auto">
          Discover amazing wilderness properties for sale, rent, or lease
        </Text>

        {/* Filters */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group mb="md">
            <IconFilter size={20} />
            <Title order={3}>Filters</Title>
            <Button variant="light" size="sm" onClick={clearFilters}>
              Clear All
            </Button>
          </Group>
          
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput
                label="Search"
                placeholder="Search by title, description, or location..."
                leftSection={<IconSearch size={16} />}
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput
                label="State"
                placeholder="e.g., CO, CA, TX"
                value={filters.state}
                onChange={(e) => handleFilterChange('state', e.target.value)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Select
                label="Property Type"
                placeholder="All Types"
                data={['House', 'Condo', 'Townhouse', 'Land', 'Cabin', 'Ranch', 'Farm', 'Other']}
                value={filters.propertyType}
                onChange={(value) => handleFilterChange('propertyType', value)}
                clearable
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Select
                label="Listing Type"
                placeholder="All Types"
                data={['For Sale', 'For Rent', 'For Lease', 'Auction']}
                value={filters.listingType}
                onChange={(value) => handleFilterChange('listingType', value)}
                clearable
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Group grow>
                <NumberInput
                  label="Min Price"
                  placeholder="0"
                  value={filters.minPrice}
                  onChange={(value) => handleFilterChange('minPrice', value)}
                  min={0}
                  prefix="$"
                  thousandSeparator=","
                />
                <NumberInput
                  label="Max Price"
                  placeholder="No limit"
                  value={filters.maxPrice}
                  onChange={(value) => handleFilterChange('maxPrice', value)}
                  min={0}
                  prefix="$"
                  thousandSeparator=","
                />
              </Group>
            </Grid.Col>
          </Grid>
        </Card>

        {/* Properties Grid */}
        {loading ? (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="md">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <Card key={i} shadow="sm" padding="lg" radius="md" withBorder>
                <Skeleton height={200} mb="md" />
                <Skeleton height={20} mb="sm" />
                <Skeleton height={16} mb="xs" />
                <Skeleton height={16} width="60%" />
              </Card>
            ))}
          </SimpleGrid>
        ) : properties.length > 0 ? (
          <>
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="md">
              {properties.map((listing) => (
                <Card key={listing.id} shadow="sm" padding="lg" radius="md" withBorder component={Link} to={`/properties/${listing.id}`} style={{ textDecoration: 'none' }}>
                  <Card.Section>
                    {listing.images && listing.images.length > 0 ? (
                      <Image
                        src={listing.images[0]}
                        height={200}
                        alt={listing.title}
                      />
                    ) : (
                      <div style={{ height: 200, backgroundColor: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <IconHome size={48} color="#6c757d" />
                      </div>
                    )}
                  </Card.Section>

                  <Stack gap="sm" mt="md">
                    <Group justify="space-between">
                      <Badge color="green" variant="light">
                        {listing.propertyType}
                      </Badge>
                      <Badge color="blue" variant="light">
                        {listing.listingType}
                      </Badge>
                    </Group>

                    <Title order={4} c="dark" lineClamp={2}>
                      {listing.title}
                    </Title>

                    <Text c="dimmed" size="sm" lineClamp={2}>
                      {listing.description}
                    </Text>

                    <Group gap="xs" c="dimmed">
                      <IconMapPin size={14} />
                      <Text size="sm">
                        {listing.city}, {listing.state}
                      </Text>
                    </Group>

                    <Group justify="space-between" mt="sm">
                      <Group gap="xs">
                        <IconCurrencyDollar size={16} color="green" />
                        <Text fw={600} c="green" size="lg">
                          ${listing.price?.toLocaleString() || 'Contact for Price'}
                        </Text>
                      </Group>
                      
                      <Group gap="xs" c="dimmed">
                        <IconCalendar size={14} />
                        <Text size="sm">
                          {new Date(listing.createdAt).toLocaleDateString()}
                        </Text>
                      </Group>
                    </Group>
                  </Stack>
                </Card>
              ))}
            </SimpleGrid>

            {/* Pagination */}
            {totalPages > 1 && (
              <Group justify="center" mt="xl">
                <Pagination
                  value={currentPage}
                  onChange={setCurrentPage}
                  total={totalPages}
                  color="green"
                />
              </Group>
            )}
          </>
        ) : (
          <Card shadow="sm" padding="xl" radius="md" withBorder>
            <Stack align="center" gap="md">
              <IconHome size={48} color="dimmed" />
              <Title order={4} c="dimmed">No properties found</Title>
              <Text c="dimmed" ta="center">
                Try adjusting your search filters or be the first to add a listing!
              </Text>
              <Button component={Link} to="/add-listing" color="green">
                Add New Listing
              </Button>
            </Stack>
          </Card>
        )}
      </Stack>
    </Container>
  );
};

export default Properties;
